import { Link } from "react-router-dom";
import { Heart, ShoppingBag } from "lucide-react";
import { Product } from "@/hooks/useProducts";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { formatPrice } from "@/lib/currency";
import { motion } from "framer-motion";
import { toast } from "sonner";
import TiltCard from "@/components/3d/TiltCard";

const ProductCard = ({ product, index = 0 }: { product: Product; index?: number }) => {
  const { addToCart } = useCart();
  const { isInWishlist, toggleWishlist } = useWishlist();
  const wishlisted = isInWishlist(product.id);

  const handleQuickAdd = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(product, product.sizes?.[1] || "M");
    toast.success(`${product.name} added to cart`);
  };

  const handleWishlist = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    toggleWishlist(product.id);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
    >
      <TiltCard tiltAmount={8} glareOpacity={0.12}>
        <Link to={`/product/${product.id}`} className="group block">
          <div className="relative overflow-hidden rounded-2xl border border-border/50 bg-card transition-all duration-300 hover:border-primary/30 hover-glow">
            <div className="relative aspect-square overflow-hidden">
              <img
                src={product.image}
                alt={product.name}
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                loading="lazy"
              />
              {product.badge && (
                <span className="absolute left-3 top-3 rounded-full bg-primary px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-primary-foreground">
                  {product.badge}
                </span>
              )}
              <button
                aria-label={wishlisted ? "Remove from wishlist" : "Add to wishlist"}
                onClick={handleWishlist}
                className={`absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full transition-all ${
                  wishlisted
                    ? "bg-primary text-primary-foreground"
                    : "bg-background/60 text-muted-foreground backdrop-blur-sm hover:bg-background/80"
                }`}
              >
                <Heart className={`h-4 w-4 ${wishlisted ? "fill-current" : ""}`} />
              </button>
              <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
              <div className="absolute bottom-3 left-3 right-3 flex gap-2 opacity-0 transition-all duration-300 group-hover:opacity-100">
                <button
                  onClick={handleQuickAdd}
                  className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-primary py-2.5 text-xs font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
                >
                  <ShoppingBag className="h-3.5 w-3.5" /> Quick Add
                </button>
              </div>
            </div>
            <div className="p-4">
              <p className="text-xs text-muted-foreground">{product.category}</p>
              <h3 className="mt-1 font-display text-sm font-semibold">{product.name}</h3>
              <div className="mt-2 flex items-center gap-2">
                <span className="font-display text-lg font-bold text-primary">{formatPrice(product.price)}</span>
                {product.originalPrice && (
                  <span className="text-xs text-muted-foreground line-through">{formatPrice(product.originalPrice)}</span>
                )}
              </div>
              <div className="mt-1 flex items-center gap-1">
                <span className="text-xs text-yellow-400">{"★".repeat(Math.floor(product.rating))}</span>
                <span className="text-xs text-muted-foreground">({product.reviews})</span>
              </div>
            </div>
          </div>
        </Link>
      </TiltCard>
    </motion.div>
  );
};

export default ProductCard;
